/**
 * The six status token triples, as the only place a component may name one.
 *
 * Tailwind v4 finds classes by scanning source text, so a class assembled at
 * runtime (`` `bg-status-${tone}` ``) is never emitted into the stylesheet —
 * it compiles, renders, and paints nothing. Every triple below is therefore
 * written out in full, literally, and `STATUS_TONE_CLASSES` is the single
 * map everything else indexes. If you add a tone, add three literal classes
 * here and the matching `--color-status-*` tokens to BOTH blocks of
 * `styles.css`; the contrast suite fails if the light block is missing one.
 *
 * The meanings are fixed (and asserted by `styles.contrast.test.ts`):
 * `live` running/healthy, `pending` queued/starting/awaiting approval,
 * `done` completed/merged — deliberately quiet, because a wall of green is
 * how an operator stops seeing the red one — `warn` a human must act,
 * `danger` failed/denied/destructive, `neutral` idle/ended/unknown.
 */

export type StatusTone =
  'live' | 'pending' | 'done' | 'warn' | 'danger' | 'neutral'

/** Every tone, in severity-ish order. Exported so tests can iterate the set
 * rather than restating it and drifting out of sync. */
export const STATUS_TONES: readonly StatusTone[] = [
  'live',
  'pending',
  'done',
  'warn',
  'danger',
  'neutral',
]

/**
 * Background + foreground + border for each tone, as one string.
 *
 * Always used as a unit. Mixing a background from one family with text from
 * another is the one way to defeat the contrast suite, which only ever
 * checks a triple against itself.
 */
export const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  live: 'bg-status-live text-status-live-fg border-status-live-border',
  pending:
    'bg-status-pending text-status-pending-fg border-status-pending-border',
  done: 'bg-status-done text-status-done-fg border-status-done-border',
  warn: 'bg-status-warn text-status-warn-fg border-status-warn-border',
  danger: 'bg-status-danger text-status-danger-fg border-status-danger-border',
  neutral:
    'bg-status-neutral text-status-neutral-fg border-status-neutral-border',
}

/**
 * Just the text colour of a tone, for the places that want the meaning
 * without a chip behind it — a count, an inline verdict, a Callout heading.
 *
 * Safe on bare `bg-canvas` and `bg-surface`: every `-fg` is held to 4.5:1
 * against both, which the contrast suite asserts precisely so this map can
 * exist without a per-site check.
 */
export const STATUS_TONE_TEXT: Record<StatusTone, string> = {
  live: 'text-status-live-fg',
  pending: 'text-status-pending-fg',
  done: 'text-status-done-fg',
  warn: 'text-status-warn-fg',
  danger: 'text-status-danger-fg',
  neutral: 'text-status-neutral-fg',
}
